import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ErrorText, Loading, cx } from '../components/ui';
import { errorMessage } from '../lib/api';
import { dateTime, timeAgo, timecode } from '../lib/format';
import { qk } from '../queries/keys';
import { fetchViewers } from '../queries/videos';

/**
 * Who watched this recording, for whoever owns it.
 *
 * The count on its own answers "did anyone", which is rarely the question:
 * what the owner sent it for is usually one named person seeing it. So every
 * viewer gets a row, with when they watched and how far they got, since a
 * recording opened and abandoned at four seconds is not one that was watched.
 *
 * People who were signed in are named. Everyone else is a row too, because
 * "three people watched it, one to the end" is a different fact from "four
 * views", and the shape of the interest is the whole point of looking. They
 * are not named, and cannot be: a share link is meant to work for whoever
 * holds it, and putting a face to somebody who was never asked for one would
 * be a different product than the one people are linking to.
 */
export function Viewers({ videoId, durationSec }: { videoId: string; durationSec: number | null }) {
  const query = useQuery({ queryKey: qk.viewers(videoId), queryFn: () => fetchViewers(videoId) });

  if (query.isPending) return <Loading label="Loading who watched" />;
  if (query.isError) {
    return (
      <div className="p-3">
        <ErrorText>{errorMessage(query.error)}</ErrorText>
      </div>
    );
  }

  const { people, anonymous, emails } = query.data;
  // Addresses from the gate that are not already a named account, so the
  // same person is not listed twice under two descriptions.
  const named = new Set(people.map((person) => person.email.toLowerCase()));
  const guests = emails.filter((one) => !named.has(one.email.toLowerCase()));

  if (people.length === 0 && anonymous.length === 0 && guests.length === 0) {
    return (
      <p className="px-3 py-8 text-center text-caption text-chalk-faint">
        Nobody has opened this yet. Everyone who does appears here: by name if they were signed in, and as a row
        either way.
      </p>
    );
  }

  return (
    <div className="space-y-4 p-1">
      <ul className="space-y-2">
        {people.map((person) => (
          <ViewerRow
            key={person.id}
            avatar={
              person.avatarUrl ? (
                <img src={person.avatarUrl} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
              ) : (
                <Monogram>{(person.name?.trim() || person.email).slice(0, 1)}</Monogram>
              )
            }
            name={person.name?.trim() || person.email}
            views={person.views}
            lastSeenAt={person.lastSeenAt}
            maxProgressSec={person.maxProgressSec}
            completed={person.completed}
            durationSec={durationSec}
          />
        ))}
        {anonymous.map((viewer, index) => (
          <ViewerRow
            key={viewer.id}
            avatar={<Monogram dim>?</Monogram>}
            name={anonymous.length > 1 ? `Anonymous viewer ${index + 1}` : 'Anonymous viewer'}
            muted
            views={viewer.views}
            lastSeenAt={viewer.lastSeenAt}
            maxProgressSec={viewer.maxProgressSec}
            completed={viewer.completed}
            durationSec={durationSec}
          />
        ))}
      </ul>

      {guests.length > 0 ? (
        <div>
          <p className="mb-1.5 text-caption text-chalk-faint">
            Gave an address to watch. Anyone can type any address, so these are not accounts.
          </p>
          <ul className="space-y-1">
            {guests.map((guest) => (
              <li key={guest.email} className="flex items-baseline justify-between gap-3 text-caption">
                <span className="min-w-0 truncate text-chalk-dim">{guest.email}</span>
                <span className="shrink-0 text-chalk-faint">{dateTime(guest.createdAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function Monogram({ children, dim = false }: { children: ReactNode; dim?: boolean }) {
  return (
    <span
      className={cx(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-hairline bg-slate-800 text-caption uppercase',
        dim ? 'text-chalk-faint' : 'text-chalk-dim',
      )}
    >
      {children}
    </span>
  );
}

/**
 * One viewer. The same row whoever they are, so the eye can compare them:
 * who, when, and how far they got.
 */
function ViewerRow({
  avatar,
  name,
  muted = false,
  views,
  lastSeenAt,
  maxProgressSec,
  completed,
  durationSec,
}: {
  avatar: ReactNode;
  name: string;
  /** Anonymous rows sit a shade back from the named ones. */
  muted?: boolean;
  views: number;
  lastSeenAt: string;
  maxProgressSec: number;
  completed: boolean;
  durationSec: number | null;
}) {
  const percent =
    durationSec && durationSec > 0 ? Math.min(100, Math.round((maxProgressSec / durationSec) * 100)) : null;

  return (
    <li className="flex items-center gap-2.5 rounded border border-hairline px-2.5 py-2">
      {avatar}
      <span className="min-w-0 flex-1">
        <span className={cx('block truncate text-body', muted ? 'text-chalk-dim' : 'text-chalk')}>{name}</span>
        {/* The date said outright, with how long ago after it: "when" is the
            question, and a relative age alone makes you hover to find out. */}
        <span className="block truncate text-caption text-chalk-faint">
          {dateTime(lastSeenAt)}
          <span className="text-chalk-faint/70"> · {timeAgo(lastSeenAt)}</span>
          {views > 1 ? ` · ${views} times` : ''}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className={cx('block text-caption', completed ? 'text-go' : 'text-chalk-dim')}>
          {completed ? 'Watched' : percent !== null ? `${percent}%` : timecode(maxProgressSec)}
        </span>
        {!completed && percent !== null ? (
          <span className="mt-1 block h-1 w-14 overflow-hidden rounded-full bg-slate-800">
            <span className="block h-full rounded-full bg-chalk-dim" style={{ width: `${percent}%` }} />
          </span>
        ) : null}
      </span>
    </li>
  );
}
